import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { useAuth } from '../contexts/AuthContext';
import { getSupportedCurrencies } from '../utils/currency';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  User, Lock, Bell, Trash2, AlertCircle, Eye, EyeOff, Loader2,
  Save, LogOut, CheckCircle2, Mail, Phone, Globe, Camera, Upload, X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const currencies = getSupportedCurrencies().slice(0, 40);

export default function Settings() {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();

  // Profile tab state
  const [profileLoading, setProfileLoading] = useState(false);
  const [pictureUploading, setPictureUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: '',
    picture: '',
    preferred_currency: 'USD',
  });

  // Security tab state
  const [securityLoading, setSecurityLoading] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  // Preferences state
  const [preferencesData, setPreferencesData] = useState({
    preferred_currency: 'USD',
    email_notifications: true,
    sms_notifications: false,
    booking_notifications: true,
    review_notifications: true,
  });

  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    if (user) {
      setProfileData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        picture: user.picture || '',
        preferred_currency: user.preferred_currency || 'USD',
      });
      setPreferencesData(prev => ({
        ...prev,
        preferred_currency: user.preferred_currency || 'USD',
      }));
    }
  }, [isAuthenticated, user, navigate]);

  const handleProfileChange = (field, value) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handlePreferencesChange = (field, value) => {
    setPreferencesData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  // Avatar upload — converts the chosen image to a base64 data URL
  // so it works offline / in demo mode without needing cloud storage.
  const MAX_AVATAR_BYTES = 2 * 1024 * 1024; // 2 MB
  const handlePictureFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file (JPG, PNG, GIF, or WebP).');
      return;
    }
    if (file.size > MAX_AVATAR_BYTES) {
      toast.error('That image is larger than 2 MB. Please choose a smaller one.');
      return;
    }
    setPictureUploading(true);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target?.result;
      if (typeof dataUrl === 'string') {
        handleProfileChange('picture', dataUrl);
        toast.success('Profile picture updated. Click "Save Changes" to keep it.');
      }
      setPictureUploading(false);
    };
    reader.onerror = () => {
      toast.error('Could not read that image. Try a different file.');
      setPictureUploading(false);
    };
    reader.readAsDataURL(file);
  };

  const handlePictureInputChange = (e) => {
    const file = e.target.files && e.target.files[0];
    handlePictureFile(file);
    // reset so selecting the same file twice still fires onChange
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemovePicture = () => {
    handleProfileChange('picture', '');
    toast('Profile picture cleared. Click "Save Changes" to keep it.');
  };

  const handlePictureDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer?.files && e.dataTransfer.files[0];
    handlePictureFile(file);
  };

  const handlePictureDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // Save profile
  const handleSaveProfile = async () => {
    setProfileLoading(true);
    const token = localStorage.getItem('token');
    const isDemo = token === 'demo-session-token';

    // Demo mode: persist locally so it survives refresh without a backend.
    const persistDemoProfile = () => {
      try {
        const existing = JSON.parse(localStorage.getItem('demo_user') || '{}');
        const merged = { ...existing, ...profileData };
        localStorage.setItem('demo_user', JSON.stringify(merged));
      } catch (_err) {
        // ignore quota / JSON errors – not fatal in demo mode
      }
    };

    try {
      if (isDemo) {
        persistDemoProfile();
        toast.success('Profile saved (demo mode). Sign back in to see changes everywhere.');
        return;
      }

      const response = await fetch(`${API_URL}/api/auth/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(profileData),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || 'Failed to update profile');
      }

      toast.success('Profile updated successfully!');
    } catch (error) {
      console.error('Error updating profile:', error);
      // If backend is unreachable but the user is in demo mode, still persist locally.
      if (isDemo) {
        persistDemoProfile();
        toast.success('Profile saved locally (demo mode).');
      } else {
        toast.error(error.message || 'Could not update profile.');
      }
    } finally {
      setProfileLoading(false);
    }
  };

  // Change password
  const handleChangePassword = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordData.new_password.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }

    setSecurityLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          current_password: passwordData.current_password,
          new_password: passwordData.new_password,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to change password');
      }

      toast.success('Password changed successfully!');
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: '',
      });
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(error.message);
    } finally {
      setSecurityLoading(false);
    }
  };

  // Save preferences
  const handleSavePreferences = async () => {
    setProfileLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/preferences`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(preferencesData),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update preferences');
      }

      toast.success('Preferences updated successfully!');
    } catch (error) {
      console.error('Error updating preferences:', error);
      toast.error(error.message);
    } finally {
      setProfileLoading(false);
    }
  };

  // Delete account
  const handleDeleteAccount = async () => {
    setDeleteLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/account`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete account');
      }

      toast.success('Account deleted. You will be logged out.');
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Error deleting account:', error);
      toast.error(error.message);
    } finally {
      setDeleteLoading(false);
    }
  };

  if (!isAuthenticated || !user) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="pt-20 pb-12">
          <div className="max-w-2xl mx-auto px-6 text-center">
            <AlertCircle className="h-12 w-12 text-amber-600 mx-auto mb-4" />
            <h1 className="text-2xl font-serif font-bold text-slate-900 mb-2">
              Please Log In
            </h1>
            <p className="text-slate-600 mb-6">You need to log in to access settings.</p>
            <Button onClick={() => navigate('/login')}>
              Go to Login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="pt-20 pb-12">
        <div className="max-w-4xl mx-auto px-6">
          {/* Header */}
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="font-serif text-3xl md:text-4xl text-slate-900 mb-2">
              Settings
            </h1>
            <p className="text-slate-600 mb-8">Manage your account and preferences</p>
          </motion.div>

          {/* Tabs */}
          <Tabs defaultValue="profile" className="space-y-6">
            <TabsList className="grid w-full grid-cols-3 lg:grid-cols-4">
              <TabsTrigger value="profile">
                <User className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Profile</span>
              </TabsTrigger>
              <TabsTrigger value="security">
                <Lock className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Security</span>
              </TabsTrigger>
              <TabsTrigger value="preferences">
                <Globe className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Preferences</span>
              </TabsTrigger>
              <TabsTrigger value="account">
                <AlertCircle className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Account</span>
              </TabsTrigger>
            </TabsList>

            {/* Profile Tab */}
            <TabsContent value="profile" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Edit Profile</CardTitle>
                  <CardDescription>Update your personal information</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name</Label>
                    <Input
                      id="name"
                      value={profileData.name}
                      onChange={(e) => handleProfileChange('name', e.target.value)}
                      placeholder="Your name"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address</Label>
                    <div className="flex gap-2">
                      <Input
                        id="email"
                        type="email"
                        value={profileData.email}
                        onChange={(e) => handleProfileChange('email', e.target.value)}
                        placeholder="your@email.com"
                      />
                      <div className="flex items-center gap-2 px-3 rounded-md border border-slate-200 bg-slate-50">
                        <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                      </div>
                    </div>
                    <p className="text-xs text-slate-500">Verified</p>
                  </div>

                  {profileData.phone ? (
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={profileData.phone}
                        readOnly
                        disabled
                        className="bg-slate-50 text-slate-500"
                      />
                      <p className="text-xs text-slate-500">
                        Phone sign-in has been retired. Your phone is kept for account recovery only —
                        contact support if you need to update it.
                      </p>
                    </div>
                  ) : null}

                  <div className="space-y-3">
                    <Label>Profile Picture</Label>
                    <div
                      onDrop={handlePictureDrop}
                      onDragOver={handlePictureDragOver}
                      className="flex flex-col sm:flex-row items-center gap-6 p-5 rounded-xl border border-dashed border-slate-300 bg-slate-50/70 hover:bg-slate-50 transition-colors"
                    >
                      {/* Avatar preview circle */}
                      <div className="relative group shrink-0">
                        <div className="h-24 w-24 rounded-full overflow-hidden ring-2 ring-white shadow-md bg-gradient-to-br from-amber-100 to-coral-100 flex items-center justify-center">
                          {profileData.picture ? (
                            <img
                              src={profileData.picture}
                              alt="Profile"
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <User className="h-10 w-10 text-amber-700/70" />
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          aria-label="Change profile picture"
                          className="absolute -bottom-1 -right-1 h-9 w-9 rounded-full bg-slate-900 text-white shadow-lg flex items-center justify-center hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2 transition"
                        >
                          <Camera className="h-4 w-4" />
                        </button>
                      </div>

                      {/* Actions */}
                      <div className="flex-1 w-full text-center sm:text-left">
                        <p className="text-sm font-medium text-slate-900">
                          Upload a profile photo
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          JPG, PNG, GIF, or WebP · up to 2 MB. Drop a file here or choose one.
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2 justify-center sm:justify-start">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={pictureUploading}
                          >
                            {pictureUploading ? (
                              <>
                                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                                Loading...
                              </>
                            ) : (
                              <>
                                <Upload className="mr-2 h-3.5 w-3.5" />
                                {profileData.picture ? 'Change photo' : 'Upload photo'}
                              </>
                            )}
                          </Button>
                          {profileData.picture && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={handleRemovePicture}
                              className="text-slate-600 hover:text-red-600"
                            >
                              <X className="mr-2 h-3.5 w-3.5" />
                              Remove
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* Hidden file input */}
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/gif,image/webp"
                        onChange={handlePictureInputChange}
                        className="hidden"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="currency">Preferred Currency</Label>
                    <Select value={profileData.preferred_currency} onValueChange={(val) => handleProfileChange('preferred_currency', val)}>
                      <SelectTrigger id="currency">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {currencies.map(c => (
                          <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <Button
                    onClick={handleSaveProfile}
                    disabled={profileLoading}
                    className="w-full bg-accent hover:bg-accent/90"
                  >
                    {profileLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="mr-2 h-4 w-4" />
                        Save Changes
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Security Tab */}
            <TabsContent value="security" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Change Password</CardTitle>
                  <CardDescription>Update your password to keep your account secure</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="current-password">Current Password</Label>
                    <div className="relative">
                      <Input
                        id="current-password"
                        type={showPasswords.current ? 'text' : 'password'}
                        value={passwordData.current_password}
                        onChange={(e) => handlePasswordChange('current_password', e.target.value)}
                        placeholder="Enter your current password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                      >
                        {showPasswords.current ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="new-password">New Password</Label>
                    <div className="relative">
                      <Input
                        id="new-password"
                        type={showPasswords.new ? 'text' : 'password'}
                        value={passwordData.new_password}
                        onChange={(e) => handlePasswordChange('new_password', e.target.value)}
                        placeholder="Enter your new password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                      >
                        {showPasswords.new ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    <p className="text-xs text-slate-500">At least 6 characters</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirm-password">Confirm New Password</Label>
                    <div className="relative">
                      <Input
                        id="confirm-password"
                        type={showPasswords.confirm ? 'text' : 'password'}
                        value={passwordData.confirm_password}
                        onChange={(e) => handlePasswordChange('confirm_password', e.target.value)}
                        placeholder="Confirm your new password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                      >
                        {showPasswords.confirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  <Button
                    onClick={handleChangePassword}
                    disabled={securityLoading}
                    className="w-full bg-accent hover:bg-accent/90"
                  >
                    {securityLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Changing...
                      </>
                    ) : (
                      <>
                        <Lock className="mr-2 h-4 w-4" />
                        Change Password
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Preferences Tab */}
            <TabsContent value="preferences" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Preferences</CardTitle>
                  <CardDescription>Customize your experience and notification settings</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="pref-currency">Default Currency</Label>
                    <Select value={preferencesData.preferred_currency} onValueChange={(val) => handlePreferencesChange('preferred_currency', val)}>
                      <SelectTrigger id="pref-currency">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {currencies.map(c => (
                          <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="border-t border-slate-200 pt-6">
                    <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <Bell className="h-4 w-4" />
                      Notification Settings
                    </h3>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <Label className="font-medium text-slate-900">Email Notifications</Label>
                          <p className="text-xs text-slate-500">Receive updates via email</p>
                        </div>
                        <Switch
                          checked={preferencesData.email_notifications}
                          onCheckedChange={(checked) => handlePreferencesChange('email_notifications', checked)}
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <Label className="font-medium text-slate-900">SMS Notifications</Label>
                          <p className="text-xs text-slate-500">Receive text message updates</p>
                        </div>
                        <Switch
                          checked={preferencesData.sms_notifications}
                          onCheckedChange={(checked) => handlePreferencesChange('sms_notifications', checked)}
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <Label className="font-medium text-slate-900">Booking Notifications</Label>
                          <p className="text-xs text-slate-500">Get alerts about booking requests</p>
                        </div>
                        <Switch
                          checked={preferencesData.booking_notifications}
                          onCheckedChange={(checked) => handlePreferencesChange('booking_notifications', checked)}
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <Label className="font-medium text-slate-900">Review Notifications</Label>
                          <p className="text-xs text-slate-500">Get notified when someone reviews</p>
                        </div>
                        <Switch
                          checked={preferencesData.review_notifications}
                          onCheckedChange={(checked) => handlePreferencesChange('review_notifications', checked)}
                        />
                      </div>
                    </div>
                  </div>

                  <Button
                    onClick={handleSavePreferences}
                    disabled={profileLoading}
                    className="w-full bg-accent hover:bg-accent/90"
                  >
                    {profileLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="mr-2 h-4 w-4" />
                        Save Preferences
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Account Tab */}
            <TabsContent value="account" className="space-y-6">
              <Card className="border-red-200 bg-red-50">
                <CardHeader>
                  <CardTitle className="text-red-900">Delete Account</CardTitle>
                  <CardDescription className="text-red-700">
                    Permanently delete your account and all associated data
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-red-700 mb-6">
                    This action cannot be undone. Please be certain before proceeding. All your properties, messages, and bookings will be permanently deleted.
                  </p>

                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive" className="w-full">
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete My Account
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Account?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will permanently delete your account and all associated data. This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <div className="py-4">
                        <p className="text-sm font-medium text-slate-900 mb-2">This will:</p>
                        <ul className="text-sm text-slate-600 space-y-1 ml-4">
                          <li>• Delete all your properties</li>
                          <li>• Remove all messages and conversations</li>
                          <li>• Cancel all active bookings</li>
                          <li>• Delete all reviews and ratings</li>
                        </ul>
                      </div>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleDeleteAccount}
                        disabled={deleteLoading}
                        className="bg-red-600 hover:bg-red-700"
                      >
                        {deleteLoading ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Deleting...
                          </>
                        ) : (
                          'Yes, Delete My Account'
                        )}
                      </AlertDialogAction>
                    </AlertDialogContent>
                  </AlertDialog>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
